import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useSettings } from '../context/SettingsContext';
import { useCRMData } from '../context/CRMDataContext';
import { getActivityColor } from '../utils/helpers';
import { toDatetimeLocalValue } from '../utils/dateTime';
import BaseModal from '../components/modals/BaseModal';
import ActivityForm from '../components/forms/ActivityForm';
import BookMeetingModal from '../components/modals/BookMeetingModal';
import {
  format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameMonth, isSameDay, getISOWeek
} from 'date-fns';
import { nb } from 'date-fns/locale';

const HIDDEN_ACTIVITY_STATUSES = new Set([
  'Gjennomført',
  'Arkivert',
  'Slettet',
  'Deleted'
]);

const isActiveActivity = (activity) => (
  !HIDDEN_ACTIVITY_STATUSES.has(activity?.status)
);

const getActivityCompanyName = (activity, companies = [], deals = []) => {
  const directCompanyId = activity?.company_id || activity?.companyId;
  const relatedDealId = activity?.deal_id || activity?.dealId;
  const relatedDeal = deals.find(deal => (
    deal?.deal_id === relatedDealId || deal?.id === relatedDealId
  ));
  const companyId = directCompanyId || relatedDeal?.company_id || relatedDeal?.companyId;

  return companies.find(company => (
    company?.company_id === companyId || company?.id === companyId
  ))?.name || '';
};

const getActivityDisplayTitle = (activity, companies = [], deals = []) => {
  const title = activity?.title || activity?.name || 'Aktivitet';
  const companyName = getActivityCompanyName(activity, companies, deals);

  return companyName ? `${companyName} – ${title}` : title;
};

const getActivityKey = (activity) => {
  if (activity?.id) return activity.id;
  if (activity?.activity_id) return activity.activity_id;

  if (import.meta.env.DEV) {
    console.warn('[Activities] Activity missing stable id', activity);
  }

  return [
    'activity-fallback',
    activity?.title || 'untitled',
    activity?.date || 'no-date',
    activity?.type || 'no-type',
    activity?.contact_id || 'no-contact',
    activity?.deal_id || 'no-deal'
  ].join('-');
};

const getActivityIcon = (type) => {
  switch (type) {
    case 'Samtale': return '📞';
    case 'Online møte': return '🎥';
    case 'E-post': return '📧';
    case 'Møte': return '👥';
    case 'Oppgave': return '✅';
    default: return '📅';
  }
};

const getCalendarActivityStyle = (activity) => {
  const start = new Date(activity.date);
  const fallbackEnd = new Date(start.getTime() + 60 * 60 * 1000);
  const end = activity.end_date ? new Date(activity.end_date) : fallbackEnd;
  const endTime = Number.isNaN(end.getTime()) || end <= start ? fallbackEnd : end;
  const durationMinutes = Math.max(30, (endTime - start) / 60000);

  return {
    height: `${durationMinutes * 2}px`,
    top: `${(start.getMinutes() % 30) * 2 + 4}px`
  };
};

const formatTimeLabel = (h, m, formatType) => {
  const minStr = m === 0 ? '00' : '30';
  if (formatType === '12h') {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minStr} ${ampm}`;
  }
  return `${h.toString().padStart(2, '0')}:${minStr}`;
};

const MemoizedListActivityCard = memo(function MemoizedListActivityCard({ activity, onOpenActivity, companies, deals }) {
  const act = activity;
  const colors = getActivityColor(act.type);

  const handleActivityClick = (e, selectedActivity) => {
    e.stopPropagation();
    onOpenActivity(selectedActivity);
  };

  return (
    <tr
      key={getActivityKey(act)}
      className="hover:bg-slate-50 cursor-pointer"
      onClick={(e) => handleActivityClick(e, act)}
    >
      <td className="px-6 py-4 whitespace-nowrap text-slate-500 font-medium">
        {format(new Date(act.date), 'dd.MM.yyyy HH:mm')}
      </td>
      <td className="px-6 py-4 font-bold text-slate-900">
        {getActivityDisplayTitle(act, companies, deals)}
      </td>
      <td className="px-6 py-4">
        <span className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest border ${colors.bg} ${colors.text} ${colors.border}`}>
          <SafeIcon icon={getActivityIcon(act.type)} className="mr-1 w-3 h-3 inline" />
          {act.type}
        </span>
      </td>
      <td className="px-6 py-4">
        <span className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest border ${act.status === 'Gjennomført' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
          {act.status}
        </span>
      </td>
    </tr>
  );
});

const MemoizedDayActivityCard = memo(function MemoizedDayActivityCard({ activity, onOpenActivity, timeFormat, companies, deals }) {
  const act = activity;
  const colors = getActivityColor(act.type);

  const handleActivityClick = (e, selectedActivity) => {
    e.stopPropagation();
    onOpenActivity(selectedActivity);
  };

  return (
    <div
      key={getActivityKey(act)}
      onClick={(e) => handleActivityClick(e, act)}
      style={getCalendarActivityStyle(act)}
      className={`absolute left-8 right-1 z-20 p-2 rounded-xl border-l-4 shadow-sm cursor-pointer ${colors.bg} ${colors.border.replace('border-', 'border-l-')}`}
    >
      <div className="flex justify-between items-start">
        <div className="min-w-0">
          <span className={`font-bold text-sm ${colors.text} block truncate`}>{getActivityDisplayTitle(act, companies, deals)}</span>
        </div>
        <span className="text-[10px] font-black text-slate-500 bg-white/60 px-2 py-0.5 rounded-lg shadow-sm">
          {formatTimeLabel(new Date(act.date).getHours(), new Date(act.date).getMinutes(), timeFormat)}
        </span>
      </div>
      <p className="text-xs text-slate-600 mt-1">{act.description || act.type}</p>
    </div>
  );
});

const MemoizedWeekActivityCard = memo(function MemoizedWeekActivityCard({ activity, onOpenActivity, companies, deals }) {
  const act = activity;
  const colors = getActivityColor(act.type);
  const displayTitle = getActivityDisplayTitle(act, companies, deals);

  const handleActivityClick = (e, selectedActivity) => {
    e.stopPropagation();
    onOpenActivity(selectedActivity);
  };

  return (
    <div
      key={getActivityKey(act)}
      onClick={(e) => handleActivityClick(e, act)}
      style={getCalendarActivityStyle(act)}
      className={`absolute left-1 right-1 z-20 p-1.5 rounded-lg border-l-2 shadow-sm overflow-hidden text-[10px] ${colors.bg} ${colors.border.replace('border-', 'border-l-')}`}
      title={displayTitle}
    >
      <span className={`font-bold ${colors.text} block truncate`}>{displayTitle}</span>
    </div>
  );
});

const MemoizedMonthActivityCard = memo(function MemoizedMonthActivityCard({ activity, onOpenActivity, timeFormat, companies, deals }) {
  const act = activity;
  const colors = getActivityColor(act.type);
  const displayTitle = getActivityDisplayTitle(act, companies, deals);

  const handleActivityClick = (e, selectedActivity) => {
    e.stopPropagation();
    onOpenActivity(selectedActivity);
  };

  return (
    <div
      key={getActivityKey(act)}
      onClick={(e) => handleActivityClick(e, act)}
      className={`truncate px-1.5 py-1 rounded text-[9px] font-bold ${colors.bg} ${colors.text}`}
      title={displayTitle}
    >
      <div className="truncate">
        {formatTimeLabel(new Date(act.date).getHours(), new Date(act.date).getMinutes(), timeFormat)} {displayTitle}
      </div>
    </div>
  );
});

const Activities = () => {
  const {
    activities = [],
    companies = [],
    deals = [],
    updateActivity
  } = useCRMData();

  const {
    calendarSettings = { workWeekOnly: true, timeFormat: '24h', coreStart: 8, coreEnd: 16 },
    updateCalendarSettings = () => {}
  } = useSettings();

  const location = useLocation();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState('list');
  const [calendarViewMode, setCalendarViewMode] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const activityCloseTimerRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedView = params.get('view');
    setViewMode(requestedView === 'calendar' ? 'calendar' : 'list');
  }, [location]);

  useEffect(() => () => {
    if (activityCloseTimerRef.current) {
      clearTimeout(activityCloseTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const container = document.getElementById('calendar-scroll-container');
    if (container && (calendarViewMode === 'day' || calendarViewMode === 'week')) {
      setTimeout(() => {
        container.scrollTop = (calendarSettings?.coreStart || 8) * 120;
      }, 10);
    }
  }, [calendarViewMode, selectedDate, calendarSettings?.coreStart]);

  const filteredActivities = (activities || [])
    .filter(isActiveActivity)
    .slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const getActivitiesForDay = (date) => {
    return filteredActivities
      .filter(a => isSameDay(new Date(a.date), date))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const openActivityModal = useCallback((activity) => {
    if (activityCloseTimerRef.current) {
      clearTimeout(activityCloseTimerRef.current);
      activityCloseTimerRef.current = null;
    }

    setSelectedActivity(activity);
    setIsModalOpen(true);
  }, []);

  const closeActivityModal = () => {
    if (!isModalOpen) return;

    setIsModalOpen(false);

    activityCloseTimerRef.current = setTimeout(() => {
      setSelectedActivity(null);
      activityCloseTimerRef.current = null;
    }, 220);
  };

  const handleDayClick = (date, hour = 12, minute = 0) => {
    const newDate = new Date(date);
    newDate.setHours(hour, minute, 0, 0);
    openActivityModal({
      date: toDatetimeLocalValue(newDate),
      end_date: toDatetimeLocalValue(new Date(newDate.getTime() + 60 * 60 * 1000)),
      type: 'Oppgave'
    });
  };

  const handleOpenActivity = useCallback((activity) => {
    openActivityModal(activity);
  }, [openActivityModal]);

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const handlePrev = () => {
    setSelectedDate((date) => {
      if (calendarViewMode === 'day') return subDays(date, 1);
      if (calendarViewMode === 'week') return subWeeks(date, 1);
      return subMonths(date, 1);
    });
  };

  const handleNext = () => {
    setSelectedDate((date) => {
      if (calendarViewMode === 'day') return addDays(date, 1);
      if (calendarViewMode === 'week') return addWeeks(date, 1);
      return addMonths(date, 1);
    });
  };

  const getHeaderTitle = () => {
    if (calendarViewMode === 'day') {
      return format(selectedDate, 'EEEE d. MMMM yyyy', { locale: nb });
    }

    if (calendarViewMode === 'week') {
      return `Uke ${String(getISOWeek(selectedDate)).padStart(2, '0')}, ${format(selectedDate, 'yyyy')}`;
    }

    return format(selectedDate, 'LLLL yyyy', { locale: nb });
  };

  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    timeSlots.push({ hour: h, minute: 0 });
    timeSlots.push({ hour: h, minute: 30 });
  }

  const renderListView = () => (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Tidspunkt</th>
            <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Tittel</th>
            <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Type</th>
            <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filteredActivities.map(act => (
            <MemoizedListActivityCard
              key={getActivityKey(act)}
              activity={act}
              companies={companies}
              deals={deals}
              onOpenActivity={handleOpenActivity}
            />
          ))}
          {filteredActivities.length === 0 && (
            <tr>
              <td colSpan="4" className="px-6 py-12 text-center text-slate-400 font-medium">Ingen aktiviteter funnet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderDayView = () => {
    const dayActivities = getActivitiesForDay(selectedDate);
    const coreStart = calendarSettings?.coreStart || 8;
    const coreEnd = calendarSettings?.coreEnd || 16;

    return (
      <div className="flex flex-col h-[650px] overflow-y-auto bg-white relative custom-scrollbar" id="calendar-scroll-container">
        <div className="flex-1 relative">
          {timeSlots.map(slot => {
            const isCore = slot.hour >= coreStart && slot.hour < coreEnd;
            const bgClass = isCore ? 'bg-white' : 'bg-slate-100/60';
            const isHour = slot.minute === 0;

            const slotActs = dayActivities.filter(a => {
              const d = new Date(a.date);
              return d.getHours() === slot.hour && (slot.minute === 0 ? d.getMinutes() < 30 : d.getMinutes() >= 30);
            });

            return (
              <div key={`${slot.hour}-${slot.minute}`} className={`flex min-h-[60px] border-b border-slate-200 ${bgClass} group`}>
                <div className={`w-16 md:w-20 flex-shrink-0 text-right pr-4 pt-1 border-r border-slate-200 ${isCore ? 'bg-slate-50/50' : 'bg-transparent'}`}>
                  <span className={`${isHour ? 'text-xs font-black text-slate-600' : 'text-[10px] font-bold text-slate-400 opacity-60'}`}>
                    {formatTimeLabel(slot.hour, slot.minute, calendarSettings?.timeFormat || '24h')}
                  </span>
                </div>
                <div
                  className="flex-1 relative cursor-pointer hover:bg-primary-50/20 transition-colors p-1"
                  onClick={() => handleDayClick(selectedDate, slot.hour, slot.minute)}
                >
                  <div className="absolute left-4 top-2 opacity-0 group-hover:opacity-100 text-primary-500 text-xs font-bold transition-opacity">
                    + Legg til
                  </div>
                  <div className="space-y-1 pl-8 relative">
                    {slotActs.map(act => (
                      <MemoizedDayActivityCard
                        key={getActivityKey(act)}
                        activity={act}
                        companies={companies}
                        deals={deals}
                        onOpenActivity={handleOpenActivity}
                        timeFormat={calendarSettings?.timeFormat || '24h'}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const numDays = calendarSettings?.workWeekOnly ? 5 : 7;
    const days = Array.from({ length: numDays }, (_, i) => addDays(weekStart, i));
    const coreStart = calendarSettings?.coreStart || 8;
    const coreEnd = calendarSettings?.coreEnd || 16;

    return (
      <div className="flex flex-col h-[650px] overflow-y-auto bg-white relative custom-scrollbar" id="calendar-scroll-container">
        <div className="flex border-b border-slate-200 sticky top-0 bg-white z-20 shadow-sm">
          <div className="w-12 md:w-16 flex-shrink-0 border-r border-slate-200 bg-slate-50"></div>
          {days.map(day => (
            <div key={day.toString()} className="flex-1 text-center py-3 border-r border-slate-200 bg-slate-50">
              <div className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest">{format(day, 'EEEE', { locale: nb }).substring(0,3)}</div>
              <div className={`text-base md:text-lg font-black mt-0.5 ${isSameDay(day, new Date()) ? 'text-primary-600 bg-primary-50 w-8 h-8 mx-auto rounded-full flex items-center justify-center' : 'text-slate-900'}`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>

        <div className="flex-1 relative">
          {timeSlots.map(slot => {
            const isCore = slot.hour >= coreStart && slot.hour < coreEnd;
            const bgClass = isCore ? 'bg-white' : 'bg-slate-100/60';
            const isHour = slot.minute === 0;

            return (
              <div key={`${slot.hour}-${slot.minute}`} className={`flex min-h-[60px] border-b border-slate-200 ${bgClass}`}>
                <div className={`w-12 md:w-16 flex-shrink-0 text-right pr-2 pt-1 border-r border-slate-200 ${isCore ? 'bg-slate-50/50' : 'bg-transparent'}`}>
                  <span className={`${isHour ? 'text-[11px] font-black text-slate-600' : 'text-[9px] font-bold text-slate-400 opacity-60'}`}>
                    {formatTimeLabel(slot.hour, slot.minute, calendarSettings?.timeFormat || '24h')}
                  </span>
                </div>
                {days.map(day => {
                  const hourActs = getActivitiesForDay(day).filter(a => {
                    const d = new Date(a.date);
                    return d.getHours() === slot.hour && (slot.minute === 0 ? d.getMinutes() < 30 : d.getMinutes() >= 30);
                  });

                  return (
                    <div
                      key={day.toString()}
                      className="flex-1 border-r border-slate-200 relative p-1 hover:bg-primary-50/30 transition-colors cursor-pointer group"
                      onClick={() => handleDayClick(day, slot.hour, slot.minute)}
                    >
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <SafeIcon icon="➕" className="w-4 h-4 text-primary-300" />
                      </div>
                      <div className="space-y-1 relative z-10">
                        {hourActs.map(act => (
                          <MemoizedWeekActivityCard
                            key={getActivityKey(act)}
                            activity={act}
                            companies={companies}
                            deals={deals}
                            onOpenActivity={handleOpenActivity}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    let days = eachDayOfInterval({ start: startDate, end: endDate });
    let weekDays = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

    if (calendarSettings?.workWeekOnly) {
      days = days.filter(d => d.getDay() !== 0 && d.getDay() !== 6);
      weekDays = ['Man', 'Tir', 'Ons', 'Tor', 'Fre'];
    }

    const gridColsClass = calendarSettings?.workWeekOnly ? 'grid-cols-5' : 'grid-cols-7';

    return (
      <div className="flex flex-col h-[650px] bg-white">
        <div className={`grid ${gridColsClass} border-b border-slate-200 bg-slate-50 shadow-sm z-10`}>
          {weekDays.map(day => (
            <div key={day} className="py-3 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">
              {day}
            </div>
          ))}
        </div>
        <div className={`flex-1 grid ${gridColsClass} auto-rows-fr overflow-y-auto custom-scrollbar bg-slate-100 gap-px`}>
          {days.map((day) => {
            const dayActs = getActivitiesForDay(day);
            const isCurrentMonth = isSameMonth(day, monthStart);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={day.toString()}
                onClick={() => handleDayClick(day, 12, 0)}
                className={`min-h-[100px] bg-white p-2 cursor-pointer hover:bg-slate-50 transition-colors ${!isCurrentMonth ? 'opacity-40' : ''}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <div className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-primary-600 text-white shadow-md' : 'text-slate-700'}`}>
                    {format(day, 'd')}
                  </div>
                </div>
                <div className="space-y-1 mt-2">
                  {dayActs.slice(0, 3).map(act => (
                    <MemoizedMonthActivityCard
                      key={getActivityKey(act)}
                      activity={act}
                      companies={companies}
                      deals={deals}
                      onOpenActivity={handleOpenActivity}
                      timeFormat={calendarSettings?.timeFormat || '24h'}
                    />
                  ))}
                  {dayActs.length > 3 && (
                    <div className="text-[9px] font-bold text-slate-400 pl-1 mt-1">
                      + {dayActs.length - 3} flere
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const isCalendarView = viewMode === 'calendar';

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between pb-6 border-b border-slate-200 gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">
            {isCalendarView ? 'Kalender' : 'Aktiviteter'}
          </h1>
          <p className="text-slate-500 font-medium text-lg mt-1">
            {isCalendarView ? 'Planlegg møter, avtaler og oppfølging i kalender.' : 'Oppgaver, møter, calls og oppfølging.'}
          </p>
        </div>
        <div className="flex gap-4 w-full md:w-auto">
          <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
            <button onClick={() => navigate('/activities?view=list')} className={`px-4 py-2 rounded-lg text-xs font-black tracking-widest uppercase transition-all ${viewMode === 'list' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}>Liste</button>
            <button onClick={() => navigate('/activities?view=calendar')} className={`px-4 py-2 rounded-lg text-xs font-black tracking-widest uppercase transition-all ${viewMode === 'calendar' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}>Kalender</button>
          </div>
          <button onClick={() => setIsMeetingModalOpen(true)} className="px-6 py-3 bg-primary-600 text-white rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all active:scale-95">
            <SafeIcon icon="🎥" /> Book Møte
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        renderListView()
      ) : (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto no-scrollbar">
              <button onClick={handleToday} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-black uppercase tracking-widest shadow-sm hover:bg-slate-50 transition-all text-slate-700 whitespace-nowrap">I dag</button>

              <div className="flex bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden shrink-0">
                <button onClick={handlePrev} className="p-2.5 hover:bg-slate-50 border-r border-slate-200 transition-colors"><SafeIcon icon="⬅️" className="w-4 h-4 text-slate-600" /></button>
                <button onClick={handleNext} className="p-2.5 hover:bg-slate-50 transition-colors"><SafeIcon icon="➡️" className="w-4 h-4 text-slate-600" /></button>
              </div>

              <h2 className="text-lg font-black text-slate-900 capitalize min-w-[150px] whitespace-nowrap ml-2">{getHeaderTitle()}</h2>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
                {['day', 'week', 'month'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => setCalendarViewMode(mode)}
                    className={`px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${calendarViewMode === mode ? 'bg-white shadow-sm text-primary-700' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {mode === 'day' ? 'Dag' : mode === 'week' ? 'Uke' : 'Måned'}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2.5 rounded-xl border transition-all ${showSettings ? 'bg-slate-200 border-slate-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                title="Kalenderinnstillinger"
              >
                <SafeIcon icon="⚙️" className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          </div>

          {showSettings && (
            <div className="bg-slate-50 border-b border-slate-200 p-4 flex flex-wrap gap-6 items-center animate-in slide-in-from-top-2">
              <div className="flex items-center gap-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Arbeidsuke</label>
                <div className="flex bg-white rounded-lg border border-slate-200 p-0.5 shadow-sm">
                  <button
                    onClick={() => updateCalendarSettings({ workWeekOnly: true })}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${calendarSettings?.workWeekOnly ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    5 Dager
                  </button>
                  <button
                    onClick={() => updateCalendarSettings({ workWeekOnly: false })}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${!calendarSettings?.workWeekOnly ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    7 Dager
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Tidsformat</label>
                <div className="flex bg-white rounded-lg border border-slate-200 p-0.5 shadow-sm">
                  <button
                    onClick={() => updateCalendarSettings({ timeFormat: '24h' })}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${calendarSettings?.timeFormat === '24h' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    24 Timer
                  </button>
                  <button
                    onClick={() => updateCalendarSettings({ timeFormat: '12h' })}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${calendarSettings?.timeFormat !== '24h' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    AM / PM
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Kjernetid (Hvit sone)</label>
                <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
                  <input
                    type="number" min="0" max="23"
                    className="w-12 text-xs font-bold text-center outline-none text-slate-700"
                    value={calendarSettings?.coreStart || 8}
                    onChange={(e) => updateCalendarSettings({ coreStart: Number(e.target.value) })}
                  />
                  <span className="text-slate-300 font-bold">-</span>
                  <input
                    type="number" min="0" max="24"
                    className="w-12 text-xs font-bold text-center outline-none text-slate-700"
                    value={calendarSettings?.coreEnd || 16}
                    onChange={(e) => updateCalendarSettings({ coreEnd: Number(e.target.value) })}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="bg-white">
            {calendarViewMode === 'day' ? renderDayView() : (calendarViewMode === 'week' ? renderWeekView() : renderMonthView())}
          </div>
        </div>
      )}

      <BaseModal isOpen={isModalOpen} onClose={closeActivityModal} title="Aktivitet" disableAnimation={true}>
        {isModalOpen && (
          <ActivityForm activity={selectedActivity} onClose={closeActivityModal} />
        )}
      </BaseModal>

      <BookMeetingModal isOpen={isMeetingModalOpen} onClose={() => setIsMeetingModalOpen(false)} />
    </div>
  );
};

export default Activities;